import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../layout/Layout";
import { ClientSelectSingle } from "../../../components/form-v2/searchable-select/ClientSelect";
import { useDevicesManagement } from "./DevicesManagementContext";
import { useQuery } from "react-query";
import { apiV2 } from "../../../api/axiosConfig";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import { Action, ActionSelect } from "../../../components/ui/ActionSelect";
import PaginationNoneFloating from "../../../components/ui/Content/PaginationNoneFloating";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { isAxiosError } from "axios";
import { BlueButton } from "../../../components/ui/Buttons";
import Modal from "../../../components/portal/Modal";
import BasicText from "../../../components/form-v2/basic-text";
import { useAuth } from "../../../context/user";
import { DepartmentSelectV2Multi } from "../../../lib/departments/DepartmentSelectV2Multi";
import { useUserManagementBrowsing } from "../../../lib/user-management/UserManagementBrowsingContext";
import { IconButton } from "../import/inputs";
import { Download } from "@mui/icons-material";
import { useDeviceExport } from "./useDeviceExport";
import { DeviceDataTypes } from "../../../types/device-data-types";
import {useCopyID} from "@/lib/misc/useCopyID";

const DeviceFilterArchiveStatus: React.FC = () => {
  const ctx = useDevicesManagement();
  const auth = useAuth();

  return (
    <div className="bg-white shadow-md p-4 flex flex-row gap-8 items-center">
      <a className={`cursor-pointer ${ctx.archived ? "opacity-50" : ""}`} onClick={() => {
        ctx.setPage(1);
        ctx.setArchived(false);
      }}>Devices ({ctx.devices?.total})</a>
        <a className={`cursor-pointer ${ctx.archived ? "" : "opacity-50"}`} onClick={() => {
            ctx.setPage(1);
            ctx.setArchived(true)
        }}>Archived</a>
        <div className={"flex-1"}/>      
        <div>
        {auth.isSuperAdmin && ctx.client &&
          <Link to="/management/devices/create">
            <div>Add Device</div>
          </Link>}
        {!auth.isSuperAdmin && <a href="/management/devices/create">Add Device</a>}
      </div>
    </div>
  );
};

const DeviceFilterSearch: React.FC = () => {
  const ctx = useDevicesManagement();
  const browsing = useUserManagementBrowsing();
  const auth = useAuth();

  const [initialDepartmentIDs, setInitialDepartmentIDs] = useState<string[]>(browsing.departmentIDs);
  const [initialClientID, setInitialClientID] = useState<string>(browsing.clientID || "");

  useEffect(() => {
    if (initialClientID !== browsing.clientID && initialClientID && browsing.clientID) {
      setInitialDepartmentIDs([]);
    }
  }, [initialClientID, browsing.clientID]);

  const clientID = useMemo(() => ctx.client?.id ?? "", [ctx.client]);
  const departmentIDs = useMemo(() => ctx.departments?.map(d => d.id) || [], [ctx.departments]);

  const { download, downloading } = useDeviceExport(clientID, departmentIDs, ctx.search);

  return (
    <div className="bg-white shadow-md p-4 flex flex-row flex-wrap gap-8">
      {auth.isSuperAdmin && (
        <div className="w-64">
          <ClientSelectSingle initialClientID={browsing.clientID} client={ctx.client} onChange={ctx.setClient} />
        </div>
      )}
              {(browsing.clientID || !auth.isSuperAdmin) && <div className="flex-1">
             <DepartmentSelectV2Multi
                initialIDs={initialDepartmentIDs}
                clientID={auth.isSuperAdmin ? browsing.clientID : undefined}
                onChange={(items) => {
                    browsing.setDepartmentIDs(items.map(d => d.id))
                    ctx.setDepartments(items)
                }}
             />
        </div>}
      {/* {(browsing.clientID || !auth.isSuperAdmin) && (
        <div className="flex-1">
          <DepartmentSelectV2Multi
            initialIDs={initialDepartmentIDs}
            clientID={auth.isSuperAdmin ? browsing.clientID : undefined}
            onChange={(items) => {
              browsing.setDepartmentIDs(items.map(d => d.id));
              ctx.setDepartments(items);
            }}
          />
        </div>
      )} */}
      <div className="w-64">
        <BasicText value={ctx.search} onChange={ctx.setSearch} placeholder="Search" />
      </div>
      {!ctx.archived && (
        <div className="w-64">
          <IconButton text="Export" icon={downloading ? <LoadingSpinner color="#fff" width={20} height={20} /> : <Download />} onClick={download} />
        </div>
      )}
    </div>
  );
};

const DeviceTable: React.FC = () => {
  const ctx = useDevicesManagement();

  return (
    <div className="bg-white shadow-md p-4">
      <DeviceTableHeader />
      <hr className="my-4" />
      <div className="flex flex-col gap-4">
        {ctx.devices?.items.map((d) => (
          <DeviceRow device={d} key={d.id} />
        ))}
        <div className="flex justify-center">
          <PaginationNoneFloating
            currentPage={ctx.page}
            itemsPerPage={ctx.limit}
            dataLength={ctx.devices?.total || 0}
            togglePageSelect={ctx.setPage}
          />
        </div>
      </div>
    </div>
  );
};

type DeviceRowProps = {
  device: DeviceDataTypes;
};

const DeviceRow: React.FC<DeviceRowProps> = ({ device }) => {
  const ctx = useDevicesManagement();
  const navigate = useNavigate();
  const [showArchive, setShowArchive] = useState(false)
  const {copyID, isCopied} = useCopyID()

  const archived = useMemo(() => {
    return device.archived;
  }, [device]);

  const name = useMemo(() => device.name, [device]);


  const archiveToggle = useCallback(async () => {
    const url = archived 
      ? `device/${device.id}/unarchive` 
      : `device/${device.id}/archive`;
    try {
      await apiV2.patch(url, {});
      ctx.refetch();
    } catch (e) {
      console.error(e);
      if (isAxiosError(e) && e?.response?.data?.error) {
        ctx.setActionError(e.response.data.error);
      }
    }
  }, [device.id]);
  
  return (
    <>
      <Modal open={showArchive} setOpen={setShowArchive}>
        {archived ? (
          <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md">
            <p className="font-semibold">Unarchive Device</p>
            <p>Please click to confirm unarchiving the device ({name})</p>
            <BlueButton text="Unarchive Device" onClick={() => {
              archiveToggle();
              setShowArchive(false);
            }} />
          </div>
        ) : (
          <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md">
            <p className="font-semibold">Archive Device</p>
            <p>Please click to confirm archival of the device ({name})</p>
            <BlueButton text="Archive Device" onClick={() => {
              archiveToggle();
              setShowArchive(false);
            }} />
          </div>
        )}
      </Modal>
      <div className="grid grid-cols-[4.2%_16.7%_20.8%_20.8%_20.8%_16.7%]">
        <div className="flex justify-center">
          <input
            type="checkbox"
            checked={!!(device.id && ctx.selected.has(device.id))}
            onChange={(e) => {
              if (!device.id) return;
              e.target.checked ? ctx.addSelected(device.id) : ctx.removeSelected(device.id);
            }}
          />
        </div>
        <div className="flex items-center">
          <Link to={`/management/devices/edit/${device.id}`} className="truncate pr-4">
            {device.name}
          </Link>
        </div>
        <div className="flex items-center">
          <Link to={`/management/devices/edit/${device.departmentID}`} className="truncate pr-4">
            {device.departmentName ? device.departmentName : "None"}
          </Link>
        </div>
        <div className="flex items-center">{device.UserName}</div>
        <div className="flex items-center">
          {device.deviceSourceAPI === "everywhere-mission-connect"
            ? "Mission Connect"
            : device.deviceSourceAPI === "everywhere-webhooks"
            ? "Webhooks"
            : device.deviceSourceAPI}
        </div>

        <div>
          {archived ? (
            <ActionSelect label="Select Action">
              <Action label="Unarchive" onSelect={() => setShowArchive(true)} />
              <Action label={isCopied(device.id!) ? "Copied!" : "Copy ID"} onSelect={() => copyID(device.id!)} keepOpen />
            </ActionSelect>
          ) : (
            <ActionSelect label="Select Action">
              <Action label="Edit" onSelect={() => navigate(`/management/devices/edit/${device.id}`)} />
              <Action label="Archive" onSelect={() => setShowArchive(true)} />
              <Action label={isCopied(device.id!) ? "Copied!" : "Copy ID"} onSelect={() => copyID(device.id!)} keepOpen />
            </ActionSelect>
          )}
        </div>
      </div>
    </>
  );
};

const DeviceTableHeader: React.FC = () => {
  const ctx = useDevicesManagement();
  return (
    <div className="grid grid-cols-[4.2%_16.7%_20.8%_20.8%_20.8%_16.7%]">
    <div className="flex justify-center">
        <input
          type="checkbox"
          onChange={(e) => {
            e.target.checked ? ctx.selectAll() : ctx.deselectAll();
          }}
        />
      </div>
      <div>Name</div>
      <div>Department</div>
      <div>Assigned User</div>
      <div>Data Source API</div>
      <div>Actions</div>
    </div>
  );
};

const MultiSelectActions: React.FC = () => {
  const ctx = useDevicesManagement();
  const [showArchive, setShowArchive] = useState(false);
  const [showUnarchive, setShowUnarchive] = useState(false);

  const count = ctx.selected.size;
  if (count === 0) return null;

  return (
    <>
      <Modal open={showArchive} setOpen={setShowArchive}>
        <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md">
          <p className="font-semibold">Archive Devices</p>
          <p>Please click to confirm archiving {count} devices</p>
          <BlueButton text={`Archive ${count} Devices`} loading={ctx.submitting} onClick={() => {
            ctx.setSubmitting(true);
            ctx.archiveMultiple().then(() => {
              setShowArchive(false);
              ctx.refetch();
            }).finally(() => ctx.setSubmitting(false));
          }} />
        </div>
      </Modal>

      <Modal open={showUnarchive} setOpen={setShowUnarchive}>
        <div className="bg-white shadow-md p-4 flex flex-col gap-4 rounded-md">
          <p className="font-semibold">Unarchive Devices</p>
          <p>Please click to confirm unarchiving {count} devices</p>
          <BlueButton text={`Unarchive ${count} Devices`} loading={ctx.submitting} onClick={() => {
            ctx.setSubmitting(true);
            ctx.unarchiveMultiple().then(() => {
              setShowUnarchive(false);
              ctx.refetch();
            }).finally(() => ctx.setSubmitting(false));
          }} />
        </div>
      </Modal>

      <div className="bg-white shadow-md p-4 mt-8">
        <div className="grid grid-cols-[10%_30%_30%_30%]">
          <div className="flex items-center">{count} Selected</div>
          <div />
          <div className="pr-4">
            <BlueButton text="Select All" onClick={ctx.selectAllPages} />
          </div>
          {!ctx.archived && (
            <div className="pl-4">
              <BlueButton text={`Archive (${count})`} onClick={() => setShowArchive(true)} />
            </div>
          )}
          {ctx.archived && (
            <div className="pl-4">
              <BlueButton text={`Unarchive (${count})`} onClick={() => setShowUnarchive(true)} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const DeviceList: React.FC = () => {
  const ctx = useDevicesManagement();
  const [searchParams, setSearchParams] = useSearchParams();
  const [message, setMessage] = useState(searchParams.get("message"));

  useEffect(() => {
    if (!message) return;
  
    const timeout = setTimeout(() => {
      setMessage(null);
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete("message");
      setSearchParams(newParams, { replace: true }); 
    }, 5000);
  
    return () => clearTimeout(timeout); 
  }, [message]);


  return (
    <Layout active="devices">
      <div className="flex flex-col gap-8">
        {ctx.actionError && (
          <div className="text-white bg-red shadow-md p-4 flex flex-row gap-8 items-center justify-center rounded-md">
            {ctx.actionError}
          </div>
        )}
        {message && (
          <div className="text-white bg-green shadow-md p-4 flex flex-row gap-8 items-center justify-center rounded-md">
            {message}
          </div>
        )}
        <DeviceFilterArchiveStatus />
        <DeviceFilterSearch />
        <DeviceTable />
      </div>
      <MultiSelectActions />
    </Layout>
  );
};

DeviceList.displayName = "DeviceList";

export default DeviceList;
